/**
 * MarketFit Component
 * 
 * Displays market fit analysis through deals, situations, and solutions.
 * Shows:
 * - Deal list with scores and status
 * - Product situations
 * - Product solutions with fit scores
 * - Summary metrics
 */

import React, { useState } from 'react';
import { Table, Progress, Tag, Tabs, Tooltip, Badge } from 'antd';
import {
  AimOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  BulbOutlined,
  SolutionOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { fetchMarketFitFromLocalBridge } from '../../../services/bridgeSessionDataAdapter';
import { isDriveFlowReportingEnabled } from '../../../services/driveFlowAdapter';
import { getProductDomainSession } from '../../../services/productDomainSessionApi';
import styles from './MarketFitComponent.module.css';

interface Deal {
  id: string;
  name: string;
  score: number;
  status: 'active' | 'pending' | 'won' | 'lost';
  value: number;
  situationId?: string;
  solutionId?: string;
}

interface Situation {
  id: string;
  name: string;
  description?: string;
  productCount: number;
  dealCount: number;
}

interface Solution {
  id: string;
  name: string;
  description?: string;
  fitScore: number;
  dealCount: number;
}

interface MarketFitComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Show compact view */
  compact?: boolean;
  /** Default active tab */
  defaultTab?: 'deals' | 'situations' | 'solutions';
  /** Callback when a deal is selected for analysis */
  onDealSelect?: (dealId: string, dealName: string) => void;
  /** Callback when a situation is selected */
  onSituationSelect?: (situationId: string, situationName: string) => void;
  /** Callback when a solution is selected */
  onSolutionSelect?: (solutionId: string, solutionName: string) => void;
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#fbbf24';
  return '#ef4444';
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'active':
      return { color: 'blue', icon: <ClockCircleOutlined /> };
    case 'pending':
      return { color: 'orange', icon: <ExclamationCircleOutlined /> };
    case 'won':
      return { color: 'green', icon: <CheckCircleOutlined /> };
    case 'lost':
      return { color: 'red', icon: <ExclamationCircleOutlined /> };
    default:
      return { color: 'default', icon: null };
  }
};

interface DealsTableProps {
  deals: Deal[];
  compact?: boolean;
  onDealSelect?: (dealId: string, dealName: string) => void;
}

const DealsTable: React.FC<DealsTableProps> = ({ deals, compact, onDealSelect }) => {
  const isClickable = !!onDealSelect;
  
  const columns = [
    {
      title: 'Deal',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Deal) => (
        <span 
          className={`${styles.dealName} ${isClickable ? styles.clickable : ''}`}
          onClick={isClickable ? () => onDealSelect(record.id, record.name) : undefined}
        >
          {name}
        </span>
      )
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      width: 120,
      render: (score: number) => (
        <Progress
          percent={score}
          size="small"
          strokeColor={getScoreColor(score)}
          format={p => `${p}`}
        />
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const config = getStatusConfig(status);
        return (
          <Tag color={config.color} icon={config.icon}>
            {status.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 100,
      render: (value: number) => (
        <span className={styles.dealValue}>{formatCurrency(value)}</span>
      )
    }
  ];

  return (
    <Table
      dataSource={deals}
      columns={columns}
      rowKey="id"
      pagination={compact ? false : { pageSize: 5 }}
      size="small"
      className={styles.table}
      onRow={(record) => ({
        onClick: isClickable ? () => onDealSelect(record.id, record.name) : undefined,
        className: isClickable ? styles.clickableRow : undefined,
      })}
    />
  );
};

interface SituationsViewProps {
  situations: Situation[];
  onSituationSelect?: (situationId: string, situationName: string) => void;
}

const SituationsView: React.FC<SituationsViewProps> = ({ situations, onSituationSelect }) => {
  const isClickable = !!onSituationSelect;
  return (
    <div className={styles.listContainer}>
      {situations.map(situation => (
        <div
          key={situation.id}
          className={`${styles.listItem} ${isClickable ? styles.clickableListItem : ''}`}
          onClick={isClickable ? () => onSituationSelect(situation.id, situation.name) : undefined}
          role={isClickable ? 'button' : undefined}
        >
          <div className={styles.listItemMain}>
            <BulbOutlined className={styles.listIcon} style={{ color: '#fbbf24' }} />
            <div className={styles.listItemContent}>
              <span className={styles.listItemName}>{situation.name}</span>
              {situation.description && (
                <span className={styles.listItemDescription}>{situation.description}</span>
              )}
            </div>
          </div>
          <div className={styles.listItemMeta}>
            <Tag color="blue">{situation.productCount} products</Tag>
            <Tag color="purple">{situation.dealCount} deals</Tag>
          </div>
        </div>
      ))}
    </div>
  );
};

interface SolutionsViewProps {
  solutions: Solution[];
  onSolutionSelect?: (solutionId: string, solutionName: string) => void;
}

const SolutionsView: React.FC<SolutionsViewProps> = ({ solutions, onSolutionSelect }) => {
  const isClickable = !!onSolutionSelect;
  return (
    <div className={styles.listContainer}>
      {solutions.map(solution => (
        <div
          key={solution.id}
          className={`${styles.listItem} ${isClickable ? styles.clickableListItem : ''}`}
          onClick={isClickable ? () => onSolutionSelect(solution.id, solution.name) : undefined}
          role={isClickable ? 'button' : undefined}
        >
          <div className={styles.listItemMain}>
            <SolutionOutlined className={styles.listIcon} style={{ color: '#818cf8' }} />
            <div className={styles.listItemContent}>
              <span className={styles.listItemName}>{solution.name}</span>
              {solution.description && (
                <span className={styles.listItemDescription}>{solution.description}</span>
              )}
            </div>
          </div>
          <div className={styles.listItemMeta}>
            <Tooltip title="Fit Score">
              <Progress
                percent={solution.fitScore}
                size="small"
                strokeColor={getScoreColor(solution.fitScore)}
                style={{ width: 80 }}
              />
            </Tooltip>
            <Tag color="purple">{solution.dealCount} deals</Tag>
          </div>
        </div>
      ))}
    </div>
  );
};

type TabKey = 'deals' | 'situations' | 'solutions';

export const MarketFitComponent: React.FC<MarketFitComponentProps> = ({
  compact = false,
  defaultTab = 'deals',
  onDealSelect,
  onSituationSelect,
  onSolutionSelect,
  ...props
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab);

  const handleTabChange = (key: string) => {
    setActiveTab(key as TabKey);
  };

  const renderContent = (data: ComponentData, loading: boolean) => {
    const deals: Deal[] = data.data.deals ?? [];
    const situations: Situation[] = data.data.situations ?? [];
    const solutions: Solution[] = data.data.solutions ?? [];
    
    const summary = data.data.summary ?? {
      total_deals: deals.length,
      active_deals: deals.filter(d => d.status === 'active').length,
      average_score: deals.length > 0 
        ? Math.round(deals.reduce((sum, d) => sum + d.score, 0) / deals.length)
        : 0,
      total_pipeline_value: deals.reduce((sum, d) => sum + d.value, 0),
      won_deals: deals.filter(d => d.status === 'won').length
    };

    const tabItems = [
      {
        key: 'deals',
        label: (
          <span className={styles.tabLabel}>
            <AimOutlined />
            Deals
            <Badge count={deals.length} showZero style={{ backgroundColor: '#818cf8' }} />
          </span>
        ),
        children: <DealsTable deals={deals} compact={compact} onDealSelect={onDealSelect} />
      },
      {
        key: 'situations',
        label: (
          <span className={styles.tabLabel}>
            <BulbOutlined />
            Situations
            <Badge count={situations.length} showZero style={{ backgroundColor: '#fbbf24' }} />
          </span>
        ),
        children: <SituationsView situations={situations} onSituationSelect={onSituationSelect} />
      },
      {
        key: 'solutions',
        label: (
          <span className={styles.tabLabel}>
            <SolutionOutlined />
            Solutions
            <Badge count={solutions.length} showZero style={{ backgroundColor: '#22c55e' }} />
          </span>
        ),
        children: <SolutionsView solutions={solutions} onSolutionSelect={onSolutionSelect} />
      }
    ];

    return (
      <div className={`${styles.marketFit} ${compact ? styles.compact : ''}`}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            <AimOutlined /> MarketFit Analysis
          </h3>
          <span className={styles.artifactName}>{data.artifact_name}</span>
        </div>

        {/* Summary Stats */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.active_deals}</span>
            <span className={styles.summaryLabel}>Active Deals</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.average_score}%</span>
            <span className={styles.summaryLabel}>Avg Score</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{formatCurrency(summary.total_pipeline_value)}</span>
            <span className={styles.summaryLabel}>Pipeline</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.won_deals}</span>
            <span className={styles.summaryLabel}>Won</span>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          className={styles.tabs}
        />
      </div>
    );
  };

  const customDataFetcher = isDriveFlowReportingEnabled()
    ? async (artifactId: string) => {
        const session = await getProductDomainSession(artifactId);
        const companyId = session?.company_id;
        if (!companyId) return null;
        return fetchMarketFitFromLocalBridge(companyId, artifactId);
      }
    : undefined;

  return (
    <BaseReportingComponent
      {...props}
      componentType="market_fit"
      customDataFetcher={customDataFetcher}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default MarketFitComponent;
